// Payment step (Option A, 2026-07-22): Stripe's Embedded Checkout mounted
// inside the wizard card — its own named place in the journey between
// "Review the cost" and "Run the analysis". The page never unloads: the
// session is created with redirect_on_completion=never, Stripe renders its
// whole checkout (email, card, Link, wallets) in an iframe here, and
// onComplete hands control back to the wizard, which polls entitlement and
// launches the run in place. This retires the full-tab redirect handoff for
// the happy path — the redirect-era return machinery stays as the recovery
// net for a tab that dies mid-payment (webhook + safety-net email still
// deliver the code out-of-band).
//
// Mount discipline: Stripe allows ONE embedded-checkout instance at a time,
// so this component owns the full lifecycle — init on mount (or when the
// client secret changes), destroy on unmount/change, and a `cancelled` latch
// so a slow init can never mount over a newer one.
import { useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";

export function PaymentStep({
  publishableKey,
  clientSecret,
  onComplete,
  onMountError,
}: {
  publishableKey: string;
  clientSecret: string;
  /** Stripe's in-card payment finished — poll entitlement, then launch. */
  onComplete: () => void;
  /** The checkout failed to mount (network / stripe-js) — the host fires
   *  the embedded-mount-error telemetry beacon (seam audit 2026-07-22). */
  onMountError?: () => void;
}) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [mountError, setMountError] = useState<string | null>(null);
  // onComplete lives in a ref so a re-render (parent state changes while the
  // iframe is up) never tears down and remounts the checkout mid-entry.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    let cancelled = false;
    let checkout: { destroy: () => void } | null = null;
    setMountError(null);
    (async () => {
      try {
        const stripe = await loadStripe(publishableKey);
        if (!stripe) throw new Error("stripe-js failed to load");
        const instance = await stripe.createEmbeddedCheckoutPage({
          clientSecret,
          onComplete: () => onCompleteRef.current(),
        });
        if (cancelled) {
          instance.destroy();
          return;
        }
        checkout = instance;
        if (mountRef.current) instance.mount(mountRef.current);
      } catch (e) {
        if (!cancelled) {
          // Human copy, not the raw error — the raw text goes to console
          // for diagnosis. The cost step is one Back away for retry.
          console.error("embedded checkout mount failed:", e);
          onMountError?.();
          setMountError(
            "The payment form couldn't load. Please check your connection and go back one step to try again.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
      checkout?.destroy();
    };
  }, [publishableKey, clientSecret]);

  if (mountError) {
    return <p className="payment-mount-error">{mountError}</p>;
  }
  return <div ref={mountRef} className="payment-embed-mount" />;
}
